import * as React from 'react';
import { formatShortHumanMonth } from '~/DateTime';

import styles from './styles.less';

const Grid: React.FunctionComponent<
	React.PropsWithChildren<{
		isAllowed(quarter: number): boolean;
		selectedQuarter: number | null;
		showingSelectedYear: boolean;
		onSelect(quarter: number): void;
	}>
> = React.memo(({ isAllowed, selectedQuarter, showingSelectedYear, onSelect }) => {
	const tmpDate = new Date();

	return (
		<div className={styles.body} role='rowgroup'>
			{[0, 1, 2, 3].map((quarter) => {
				const disabled = !isAllowed(quarter);

				return (
					<div
						className={`${styles.quarter} ${disabled ? styles.disabled : ''}`}
						key={quarter}
						role='row'
						onClick={
							disabled
								? undefined
								: () => {
										onSelect(quarter);
								  }
						}
					>
						{[0, 1, 2].map((month) => {
							tmpDate.setMonth(quarter * 3 + month);
							return (
								<div
									className={`${styles.quarterMonth} ${
										showingSelectedYear && selectedQuarter === quarter ? styles.selected : ''
									}`}
									key={month}
									role='gridcell'
								>
									{formatShortHumanMonth(tmpDate, false)}
								</div>
							);
						})}
					</div>
				);
			})}
		</div>
	);
});

Grid.displayName = 'DatePicker.Grid';

export const QuarterPicker: React.FunctionComponent<
	React.PropsWithChildren<{
		allowedRange?: [Date | null, Date | null];
		disabled?: boolean;
		value: Date | null;
		onSelect(date: Date): void;
	}>
> = ({ allowedRange = [null, null], disabled = false, value, onSelect }) => {
	const [year, setYear] = React.useState(() => (value ?? new Date()).getFullYear());
	const selectedQuarter = React.useMemo(() => (value !== null ? Math.floor(value.getMonth() / 3) : null), [value]);
	const showingSelectedYear = React.useMemo(() => year === value?.getFullYear(), [value, year]);
	const [fromDate, toDate] = allowedRange;

	const selectQuarter = (quarter: number) => {
		const date = new Date();
		date.setFullYear(year);
		date.setMonth(quarter * 3);
		date.setDate(1);
		onSelect(date);
	};

	const isAllowed = React.useCallback(
		(quarter: any) => {
			if (disabled) {
				return false;
			}
			if (fromDate !== null && fromDate.getFullYear() === year && quarter < Math.floor(fromDate.getMonth() / 3)) {
				return false;
			}
			if (toDate !== null && toDate.getFullYear() === year && Math.floor(toDate.getMonth() / 3) < quarter) {
				return false;
			}
			return true;
		},
		[disabled, fromDate, toDate, year]
	);

	return (
		<div className={styles.container}>
			<div className={styles.wrapper}>
				<div className={styles.navBar}>
					{fromDate === null || fromDate.getFullYear() < year ? (
						<span
							aria-label='Previous Month'
							className={styles['navButton--prev']}
							role='button'
							onClick={() => {
								setYear(year - 1);
							}}
						/>
					) : null}

					{toDate === null || toDate.getFullYear() > year ? (
						<span
							aria-label='Next Month'
							className={styles['navButton--next']}
							role='button'
							onClick={() => {
								setYear(year + 1);
							}}
						/>
					) : null}
				</div>
				<div className={styles.months}>
					<div className={styles.month} role='grid'>
						<div className={styles.caption} role='heading'>
							{year}
						</div>
						<Grid
							isAllowed={isAllowed}
							selectedQuarter={selectedQuarter}
							showingSelectedYear={showingSelectedYear}
							onSelect={selectQuarter}
						/>
					</div>
				</div>
			</div>
		</div>
	);
};

QuarterPicker.displayName = 'DatePicker.QuarterPicker';
